import { Container, SimpleGrid, GridItem } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Formik, Form } from 'formik';
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import { FormikDateRangePicker, FormikSelectAsyncPaginate } from 'components/Formik';
import Title from 'components/Title';
import { fetchPaginatedEmployee } from 'hooks/employee/useFetchPaginatedEmployee';
import { fetchPaginatedJobs } from 'hooks/jobs/useFetchPaginatedJobs';
import { useFetchScheduleReportByEmpoyee } from 'hooks/reports/useFetchScheduleReportByEmpoyee';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import ScheduleReportByUserTable from './ScheduleReportByUserTable';
import { initialScheduleReportByUserFormData, scheduleReportByUserValidationSchema } from './schema';

const ScheduleReportByUser: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'reports']);
  const formikRef = useRef<any>();

  const [reports, setReports] = useState<any>();
  const [selectedJobs, setSelectedJobs] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[] | null>([]);

  const { status, mutateAsync } = useFetchScheduleReportByEmpoyee();

  const handleFormSubmit = (values: typeof initialScheduleReportByUserFormData) => {
    const postData = {
      type: 'scheduleReport',
      user_ids: Array.isArray(selectedUsers) && selectedUsers.length ? selectedUsers.map(user => user.userId) : [],
      job_ids: Array.isArray(selectedJobs) && selectedJobs.length ? selectedJobs.map(job => job.id) : [],
      start_date: values.date_range[0] !== null ? format(values.date_range[0], 'MM-dd-yyyy') : '',
      end_date: values.date_range[1] !== null ? format(values.date_range[1], 'MM-dd-yyyy') : '',
    };

    mutateAsync(postData).then(data => {
      setReports(data?.payload);
    });
  };

  const jobLoadOptions = async (search: any, page: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedJobs, { search, page, job_status: 1 });
  };

  const employeeLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedEmployee, {
      page,
      search,
      active: '1',
      report: true,
    });
  };

  return (
    <div>
      <Title>{t('reports:scheduleReportByUser')}</Title>
      <Container mt="5" maxW="container.sm" p="0">
        <Formik
          initialValues={initialScheduleReportByUserFormData}
          validationSchema={scheduleReportByUserValidationSchema}
          onSubmit={handleFormSubmit}
        >
          {formikBag => {
            formikRef.current = formikBag;

            return (
              <Form autoComplete="off">
                <SimpleGrid columns={{ base: 1, sm: 2 }} gap={{ base: 0, sm: 3 }}>
                  <GridItem>
                    <FormikSelectAsyncPaginate
                      name="user_ids"
                      label={t('common:selectUser')}
                      loadOptions={employeeLoadOptions}
                      onChangeCallback={(data: any) => {
                        setSelectedUsers(data);
                      }}
                      selectProps={{
                        isMulti: true,
                        placeholder: t('common:allUsers'),
                        getOptionLabel: (option: any) => option?.firstName + ' ' + option?.lastName,
                        getOptionValue: (option: any) => option.userId,
                      }}
                    />
                  </GridItem>
                  <GridItem>
                    <FormikSelectAsyncPaginate
                      name="job_ids"
                      label={t('common:selectJob')}
                      loadOptions={jobLoadOptions}
                      onChangeCallback={(data: any) => {
                        setSelectedJobs(data);
                      }}
                      selectProps={{
                        isMulti: true,
                        placeholder: t('common:allJobs'),
                        getOptionLabel: (option: any) => option.name,
                        getOptionValue: (option: any) => option.id,
                      }}
                    />
                  </GridItem>
                  <GridItem>
                    <FormikDateRangePicker
                      label={t('common:dateRange')}
                      name="date_range"
                      // datePickerProps={{
                      //   maxDate: addMonthsToDate(new Date(values.date_range[0] ?? ''), 2),
                      // }}
                    />
                  </GridItem>
                  <GridItem textAlign="right" colStart={1} colEnd={-1}>
                    <Button type="submit" colorScheme="secondary" isLoading={status === 'loading'} alignSelf="flex-end">
                      {t('reports:generateReport')}
                    </Button>
                  </GridItem>
                </SimpleGrid>
              </Form>
            );
          }}
        </Formik>
      </Container>
      {reports?.jobs ? (
        <SimpleGrid columns={{ base: 1, sm: 1 }} gap={{ base: 0, sm: 10 }}>
          <GridItem>
            <ScheduleReportByUserTable
              data={reports?.jobs}
              dateRange={formikRef?.current?.values?.date_range}
              attributes={reports?.attributes_jobs}
              integrations={reports?.integrations}
              is_job_report={true}
            ></ScheduleReportByUserTable>
          </GridItem>
        </SimpleGrid>
      ) : null}
    </div>
  );
};

export default ScheduleReportByUser;
